import React, { useState } from "react";
import { Link } from "react-router-dom";

const BestServices = () => {
  const [selectedStep, setSelectedStep] = useState(2); // Default to step 2 (middle card)

  const steps = [
    {
      id: 1,
      number: "01",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      title: "Apply Online",
      description: "Fill out our simple online application form with basic details. Takes less than 5 minutes.",
      bullets: [
        "2 minutes to complete",
        "No paperwork required",
        "Instant acknowledgment"
      ]
    },
    {
      id: 2,
      number: "02",
      icon: (
        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: "Quick Verification",
      description: "Our team reviews your application and conducts verification within 24-48 hours.",
      bullets: [
        "5 minutes approval",
        "Digital verification",
        "Instant credit decision"
      ]
    },
    {
      id: 3,
      number: "03",
      icon: (
        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: "Get Funded",
      description: "Upon approval, funds are disbursed directly to your account within 24 hours.",
      bullets: [
        "Same-day disbursal",
        "Direct bank transfer",
        "Track your application"
      ]
    }
  ];

  return (
    <section className="bg-white py-12 md:py-16 lg:py-20 px-4 md:px-8 lg:px-16">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-800 mb-4">
            Simple Application Process
          </h2>
          <p className="text-base md:text-lg text-slate-600">
            Get started with your financial journey in just three easy steps.
          </p>
        </div>

        {/* Step Details Cards with Icons Above */}
        <div className="relative grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-10 md:mb-12">
          {steps.map((step) => {
            const isSelected = selectedStep === step.id;
            return (
              <div 
                key={step.id} 
                className="relative flex flex-col items-center cursor-pointer"
                onClick={() => setSelectedStep(step.id)}
              >
                {/* Icon Circle - Positioned above card */}
                <div className="relative mb-4 md:mb-5 z-10">
                  <div
                    className={`w-24 h-24 rounded-full flex items-center justify-center transition-all duration-300 ${
                      isSelected
                        ? "text-white"
                        : "border-2 border-blue-300 text-blue-600 bg-blue-50"
                    }`}
                    style={{ 
                      width: '96px', 
                      height: '96px',
                      backgroundColor: isSelected ? '#18346A' : undefined
                    }}
                  >
                    {step.icon}
                  </div>
                  {/* Number Badge - Dark grey oval */}
                  <div className="absolute -top-1 -right-1 w-10 h-6 md:w-12 md:h-7 rounded-full bg-slate-700 flex items-center justify-center">
                    <span className="text-white text-xs md:text-sm font-semibold">{step.number}</span>
                  </div>
                </div>
                
                {/* Card */}
                <div
                  className={`border-2 border-dashed rounded-lg p-4 md:p-5 relative z-10 transition-all duration-300 w-full md:w-[338px] md:h-[248px] flex flex-col ${
                    isSelected ? "bg-blue-50 border-slate-300" : "bg-white border-slate-300"
                  }`}
                >
                  <h3 className="text-lg md:text-xl font-bold text-blue-600 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-sm md:text-base text-slate-600 mb-3 leading-relaxed">
                    {step.description}
                  </p>
                  <ul className="space-y-1.5 flex-grow">
                    {step.bullets.map((bullet, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-blue-600 mt-0.5 text-sm">•</span>
                        <span className="text-xs md:text-sm text-slate-600">{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA Button */}
        <div className="text-center">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-8 py-4 rounded-lg text-base md:text-lg font-semibold hover:bg-blue-700 transition-colors shadow-md"
          >
            Get Funds Now
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default BestServices;

